// Hält die Liefertermin-Properties jeder Produktzeile aktuell. Quelle ist der
// pro Zeile serverseitig FRISCH berechnete Termin (data-hali-liefertermin aus
// cart-products.liquid), nicht mehr der beim Add-to-Cart eingefrorene String —
// so ist der Termin auch dann korrekt, wenn der Kunde erst Tage/Wochen später
// bestellt.
//
// Geschrieben werden zwei Properties je Zeile:
//  - verstecktes "_liefertermin" (pro Artikel, frischer Wert; landet im Auftrag)
//  - sichtbares "Liefertermin" (= LÄNGSTER Termin über alle Artikel). Sichtbare
//    Properties (ohne führenden Unterstrich) rendert Shopify im Checkout unter
//    dem Artikel. Der längste, weil die Bestellung komplett zusammen verschickt
//    wird — sonst wäre die Checkout-Anzeige widersprüchlich.
//
// Bundle-Zeilen (Shopify Bundles): Die Bundle-Variante trägt keine PIM-Metafelder,
// das PDP-Formular kann daher weder _lieferart noch einen Montagepreis mitgeben.
// cart-products.liquid rechnet diese Werte aus den Komponenten
// (snippets/hali-bundle-item-data.liquid) und rendert sie als data-hali-* auf die
// Zeile; hier werden sie als Properties nachgeschrieben:
//  - "_montage_preis"  Summe je Bundle-Einheit (wie bei Einzelprodukten, netto)
//  - "_lieferart"      dominante Lieferart der Komponenten (BML vor POST)
//  - "_komponenten"    JSON je Komponente (variant_id, lieferart, liefertermin, …)
// Shopify löst die Bundle-Zeile beim Checkout in Komponenten-Positionen OHNE
// Attribute auf; die Properties der Bundle-Zeile landen im Auftrag an
// lineItemGroup.customAttributes. Von dort ordnet der Order-Export sie über
// _komponenten (variant_id) den Komponenten-Positionen zu.

const HALI_LT_HIDDEN = '_liefertermin'; // intern, pro Artikel (versteckt)
const HALI_LT_VISIBLE = 'Liefertermin'; // sichtbar im Checkout (= längster)

// data-hali-*-Attribut der Bundle-Zeile → Property. Nur Bundle-Zeilen tragen die
// Attribute; bei Einzelprodukten bleiben die PDP-Werte unangetastet.
const HALI_BUNDLE_PROPS = [
  ['data-hali-montage-preis', '_montage_preis'],
  ['data-hali-lieferart', '_lieferart'],
  ['data-hali-komponenten', '_komponenten'],
];

// Ist das Montageprodukt im Warenkorb, ist der konkrete Liefertermin hinfällig
// (Termin wird telefonisch geklärt). Der Hinweis dazu steht an der Montagezeile
// und wird von cart-montage.js gesetzt; hier wird auf den Produktzeilen nur der
// dann ungültige sichtbare Datums-Termin wieder entfernt.
const HALI_MONTAGE_HANDLE = 'montage';

// Gleiche Vergleichsmetrik wie cart-summary.liquid: Monat*31 + Tag auf das
// END-Datum. Reicht für die max. ~6 Wochen Lieferspanne (Jahreswechsel ist
// dieselbe bekannte Einschränkung wie im Liquid-Pendant).
const HALI_LT_MONTHS = ['Jan.', 'Feb.', 'Mär.', 'Apr.', 'Mai', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Okt.', 'Nov.', 'Dez.'];

function haliLtEndScore(termin) {
  const parts = String(termin).split('–'); // en-dash, wie in hali-lieferzeit-dates.liquid
  if (parts.length !== 2) return null;
  const tokens = parts[1].trim().split(/\s+/);
  const day = parseInt(tokens[0].replace('.', ''), 10);
  const monthIdx = HALI_LT_MONTHS.indexOf(tokens[1]);
  if (Number.isNaN(day) || monthIdx < 0) return null;
  return (monthIdx + 1) * 31 + day;
}

let haliLtRunning = false;
let haliLtQueued = false;

async function haliLtSync() {
  if (haliLtRunning) { haliLtQueued = true; return; }
  haliLtRunning = true;
  try {
    // Frische, serverseitig neu berechnete Werte aus dem DOM lesen (je Item-Key).
    // Quelle: cart-products.liquid rendert data-hali-liefertermin pro Zeile,
    // bei Bundle-Zeilen zusätzlich data-hali-montage-preis/-lieferart/-komponenten.
    const freshByKey = new Map();
    const bundleByKey = new Map();
    document.querySelectorAll('.cart-items__table-row[data-key]').forEach((row) => {
      const key = row.getAttribute('data-key');
      if (!key) return;
      const fresh = row.getAttribute('data-hali-liefertermin');
      if (fresh) freshByKey.set(key, fresh);

      const bundleProps = {};
      let hasBundleProps = false;
      HALI_BUNDLE_PROPS.forEach(([attr, prop]) => {
        const value = row.getAttribute(attr);
        if (value === null || value === '') return;
        bundleProps[prop] = value;
        hasBundleProps = true;
      });
      if (hasBundleProps) bundleByKey.set(key, bundleProps);
    });

    const cart = await fetch('/cart.js').then((r) => r.json());

    // Pro Artikel den frisch berechneten Termin bestimmen; längsten über alle finden.
    // freshForItem[i] = nur ECHT neu berechneter Wert (wird zurückgeschrieben);
    // für die Längsten-Suche zusätzlich der gespeicherte Wert als Fallback.
    let longest = null;
    let longestScore = -1;
    const freshForItem = cart.items.map((item) => {
      const stored = item.properties && item.properties[HALI_LT_HIDDEN];
      const fresh = freshByKey.get(item.key) || null;
      const effective = fresh || stored || null;
      if (effective) {
        const score = haliLtEndScore(effective);
        if (score != null && score > longestScore) {
          longestScore = score;
          longest = effective;
        }
      }
      return fresh;
    });

    // Mit Montage: sichtbaren Datums-Termin entfernen (Termin wird telefonisch geklärt).
    // Ohne Montage, ohne irgendeinen Termin und ohne Bundle-Zeile: nichts zu tun.
    const hasMontage = cart.items.some((it) => it.handle === HALI_MONTAGE_HANDLE);
    if (!hasMontage && !longest && bundleByKey.size === 0) return;

    let montageChanged = false;

    for (let i = 0; i < cart.items.length; i += 1) {
      const item = cart.items[i];
      const stored = item.properties && item.properties[HALI_LT_HIDDEN];
      const bundleProps = bundleByKey.get(item.key) || null;
      // Nur Zeilen mit (gespeichertem oder frisch berechnetem) Lieferdatum oder
      // Bundle-Werten anfassen.
      if (!stored && !freshForItem[i] && !bundleProps) continue;

      const props = Object.assign({}, item.properties);
      let changed = false;

      // Verstecktes _liefertermin auf den frischen Wert auffrischen (landet so
      // aktuell im Auftrag/Admin). Nur wenn ein frischer Wert vorliegt.
      if (freshForItem[i] && props[HALI_LT_HIDDEN] !== freshForItem[i]) {
        props[HALI_LT_HIDDEN] = freshForItem[i];
        changed = true;
      }

      // Sichtbares Liefertermin (Checkout): ohne Montage der längste Termin,
      // mit Montage entfernen.
      if (hasMontage) {
        if (props[HALI_LT_VISIBLE] !== undefined) {
          delete props[HALI_LT_VISIBLE];
          changed = true;
        }
      } else if (longest && props[HALI_LT_VISIBLE] !== longest) {
        props[HALI_LT_VISIBLE] = longest;
        changed = true;
      }

      // Bundle-Zeile: aus den Komponenten berechnete Werte nachschreiben.
      if (bundleProps) {
        Object.entries(bundleProps).forEach(([prop, value]) => {
          if (props[prop] === value) return;
          props[prop] = value;
          changed = true;
          if (prop === '_montage_preis') montageChanged = true;
        });
      }

      if (!changed) continue; // idempotent → kein unnötiger Write, kein Loop

      // Über den Zeilen-Index (1-basiert) ändern; bewusst OHNE `sections` (kein
      // Re-Render → kein cart:update → kein Loop). Property im Cart ausgeblendet.
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: i + 1, quantity: item.quantity, properties: props }),
      });
    }

    // Der Montage-Sync (cart-montage.js) liest den Montagepreis von Bundle-Zeilen
    // primär aus dem DOM, als Fallback aus der Property. Lag beim letzten Lauf noch
    // keine gemorphte Zeile vor, stimmt die Montagezeile erst nach diesem Nachziehen.
    if (montageChanged && typeof window.syncMontage === 'function') {
      window.syncMontage();
    }
  } catch (e) {
    // Lieferdatum im Checkout ist Zusatzinfo — Fehler dürfen den Cart nicht stören.
  } finally {
    haliLtRunning = false;
    if (haliLtQueued) { haliLtQueued = false; haliLtSync(); }
  }
}

let haliLtTimer = null;
function haliLtScheduleSync() {
  // Kurz entprellen: bündelt die cart:update-Bursts (Add → Montage → …) und
  // lässt den Montage-Sync zuerst settlen.
  clearTimeout(haliLtTimer);
  haliLtTimer = setTimeout(haliLtSync, 250);
}

document.addEventListener('cart:update', haliLtScheduleSync);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', haliLtScheduleSync);
} else {
  haliLtScheduleSync();
}
